import { groupByUrgency, stillOwned, type WineListRow } from "@corklog/shared";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { fetchWines } from "../api.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { WineRow } from "../components/WineRow.js";
import { useReload } from "../offline.js";

/**
 * What to open, and why this bottle rather than another.
 *
 * Task `8.1`, and the third of the three questions `design-direction.md` says
 * this application is for — *what do I own, where is this bottle, what should
 * I open tonight*. The first two are the wine list; this is the third, and it
 * is the one the spreadsheet could never answer at all.
 *
 * **Grouped by what is running out, not by the four states.** Measured on
 * 2026-09-15: 83 of the 106 wines still holding bottles carry a lone `drinkBy`
 * and nothing else, which evaluates to *ready* for every year before it — so a
 * page grouped on the states would be one heading with eighty-four wines under
 * it and three empty headings beside it. The rule is
 * `shared/src/drinking-urgency.ts`, decided in `project-scope.md`, and it is
 * in `shared/` because the monthly report at `8.8` carries these same lists
 * into an email. **Since `8.8` that includes the grouping itself** — this
 * screen assembled its own groups until the report needed the same ones, which
 * is the drift `8.1` predicted arriving exactly where it said it would.
 *
 * **The meal is not on this page, and was for one day.** `10.1` built *what
 * suits dinner* into it on the argument that the meal is one more filter on
 * the question asked here. Both lists are drawn with `WineRow`, so the
 * recommendation and the urgency groups sat in identical rows saying opposite
 * things, and a wine turning up here for the other list's reason is the page
 * lying about why a bottle is on it. It is *Till maten* now, with a section of
 * its own; reversed in `project-scope.md` on 2026-09-18.
 *
 * **Nothing new on the server.** The list route already sends the four years,
 * their provenance and where the bottles stand, and the grouping is a pure
 * rule over rows the browser has anyway. That is also why this page works in
 * front of a cabinet with one bar of signal, like the list it is a view of.
 */
const CURRENT_YEAR = new Date().getFullYear();

type State =
  | { status: "loading" }
  | { status: "ready"; wines: WineListRow[] }
  | { status: "failed" };

export function ToOpen() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(() => {
    void fetchWines(cellarId)
      .then((wines) => setState({ status: "ready", wines }))
      .catch(() => setState({ status: "failed" }));
  }, [cellarId]);

  useReload(load);

  const wines = state.status === "ready" ? state.wines : [];
  // The grouping, the order and the caveat come from `shared/` so that the
  // monthly report's copy of this list cannot drift from this one. Ownership
  // is asked here, because the archive the report reads counts bottles
  // differently from the list contract this page holds.
  const groups = useMemo(
    () => groupByUrgency(wines.filter(stillOwned), CURRENT_YEAR),
    [wines],
  );

  /**
   * The cellar this page is not showing, counted.
   *
   * A view of what to drink that silently omitted a fifth of the cellar would
   * be lying about the cellar, so the two kinds of silence are named at the
   * foot: wines whose window is real but says nothing about this year, and
   * wines with no window at all. The second is the one with somewhere to go —
   * the walk through the cabinets, and the button on the wine's own page.
   */
  const owned = wines.filter(stillOwned);
  const listed = groups.reduce((total, group) => total + group.wines.length, 0);
  const unknown = owned.filter(
    (wine) =>
      wine.drinkFrom === null &&
      wine.peakFrom === null &&
      wine.peakTo === null &&
      wine.drinkBy === null,
  ).length;
  const quiet = owned.length - listed - unknown;

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="toOpen" />
      <p className="text-muted mt-2 max-w-prose text-sm">{t("toOpen.intro")}</p>
      {/* The one thing on this page that is work rather than reading, and it
          is a link rather than a section in the row above: the prompt to move
          a bottle to the fridge belongs where *what should I open tonight* is
          asked. Unconditional, because knowing whether there is anything to
          carry means knowing which cabinet is the fridge — which is a request,
          and this page is one of the two that must work in a basement. */}
      <p className="text-muted mt-2 max-w-prose text-sm">
        {t("toOpen.toServing")}{" "}
        <Link
          to={`/kallare/${cellarId}/servering`}
          className="text-accent underline-offset-4 hover:underline"
        >
          {t("toOpen.toServingHint")}
        </Link>
      </p>

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("wines.loadFailed")}
        </p>
      )}

      {state.status === "ready" && (
        <>
          {groups.length === 0 ? (
            <p className="text-muted mt-8 max-w-prose text-sm">{t("toOpen.empty")}</p>
          ) : (
            groups.map((group) => (
              <section key={group.urgency} className="mt-10">
                <div className="flex items-baseline justify-between gap-4">
                  <h2 className="font-display text-ink text-base">
                    {t(`toOpen.group.${group.urgency}.title`)}
                  </h2>
                  <span className="text-muted shrink-0 text-sm">
                    {t("toOpen.count", { count: group.wines.length })}
                  </span>
                </div>
                <p className="text-muted mt-1 max-w-prose text-sm">
                  {t(`toOpen.group.${group.urgency}.note`)}
                  {group.allOnImport && <> {t("toOpen.restsOnImportAll")}</>}
                </p>

                <ul className="border-line mt-4 border-t">
                  {group.wines.map(({ wine, onImport }) => (
                    <Link
                      key={wine.id}
                      to={`/kallare/${cellarId}/viner/${wine.id}`}
                      className="block"
                      // An <article> inside a link takes the link's name away
                      // from it — see the wine list, where a screen reader
                      // otherwise announces "link" once per wine and nothing
                      // else.
                      aria-label={wine.name}
                    >
                      <WineRow
                        currentYear={CURRENT_YEAR}
                        // On the row where it distinguishes this year from the
                        // one beside it, and on the heading instead where it
                        // is true of everything under it. Spread rather than
                        // passed as undefined, which under
                        // `exactOptionalPropertyTypes` is a different thing
                        // from not passing it at all.
                        {...(onImport && !group.allOnImport
                          ? { caveat: t("toOpen.restsOnImport") }
                          : {})}
                        wine={{
                          number: wine.number,
                          name: wine.name,
                          producer: wine.producer,
                          region: wine.region,
                          countryCode: wine.countryCode,
                          vintage: wine.isNonVintage ? null : wine.vintage,
                          window: {
                            drinkFrom: wine.drinkFrom,
                            peakFrom: wine.peakFrom,
                            peakTo: wine.peakTo,
                            drinkBy: wine.drinkBy,
                          },
                          standing: wine.standing,
                          away: wine.away,
                          ratings: wine.ratings,
                        }}
                      />
                    </Link>
                  ))}
                </ul>
              </section>
            ))
          )}

          {(quiet > 0 || unknown > 0) && (
            <p className="text-muted mt-10 max-w-prose text-sm">
              {quiet > 0 && t("toOpen.rest.quiet", { count: quiet })}
              {quiet > 0 && unknown > 0 && " "}
              {unknown > 0 && (
                <>
                  {t("toOpen.rest.unknown", { count: unknown })}{" "}
                  <Link
                    to={`/kallare/${cellarId}/luckor`}
                    className="text-accent underline-offset-4 hover:underline"
                  >
                    {t("toOpen.rest.unknownHint")}
                  </Link>
                </>
              )}
            </p>
          )}
        </>
      )}
    </section>
  );
}
